/**
 * Generate contextual hints for multi-move puzzles
 * Returns appropriate hint based on current move index
 */

export function getContextualHint(
    puzzle: {
        moves: string;
        hint: string;
        themes: string[];
    },
    currentMoveIndex: number
): string {
    // Parse moves - filter out move numbers and empty strings
    const moveParts = puzzle.moves.split(' ').filter(m => {
        if (!m || m.trim() === '') return false;
        if (m.match(/^\d+\.?$/)) return false;
        if (m.match(/^(1-0|0-1|1\/2-1\/2|\*)$/)) return false;
        return true;
    });
    
    const totalMoves = moveParts.length;
    
    // Single move puzzle - use original hint
    if (totalMoves <= 1) {
        return puzzle.hint;
    }
    
    // Multi-move puzzle - generate contextual hints
    const themeSet = new Set(puzzle.themes.map(t => t.toLowerCase()));
    const currentMove = moveParts[currentMoveIndex];
    
    // Move 1 (Initial move)
    if (currentMoveIndex === 0) {
        // Use original hint for first move, but make it more specific
        if (themeSet.has('mate') || themeSet.has('matein2') || themeSet.has('matein3')) {
            if (totalMoves === 2) return 'Start with a forcing check or capture.';
            if (totalMoves === 3) return 'Begin the mating sequence with a strong move.';
            return 'Start the combination - find the first forcing move.';
        }
        
        if (themeSet.has('fork')) {
            return 'Set up the fork - look for the key tactical blow.';
        }
        
        if (themeSet.has('pin') || themeSet.has('skewer')) {
            return 'Find the pinning/skewering move.';
        }
        
        if (themeSet.has('sacrifice')) {
            return 'Consider a forcing sacrifice to break through.';
        }
        
        if (themeSet.has('deflection') || themeSet.has('attraction')) {
            return 'Find the move that deflects or attracts the defender.';
        }
        
        if (themeSet.has('discoveredattack')) {
            return 'Look for a discovered attack.';
        }
        
        // Default first move hint
        return puzzle.hint || 'Find the best first move in this position.';
    }
    
    // Move 2 (Second move)
    if (currentMoveIndex === 1) {
        if (themeSet.has('mate') || themeSet.has('matein2')) {
            if (totalMoves === 2) return 'Now deliver checkmate!';
            return 'Continue the attack - find the winning continuation.';
        }
        
        if (themeSet.has('fork')) {
            return 'Execute the fork and win material.';
        }
        
        if (themeSet.has('crushing') || themeSet.has('advantage')) {
            return 'Capitalize on your advantage - find the follow-up.';
        }
        
        if (themeSet.has('pin') || themeSet.has('skewer')) {
            return 'Exploit the pin/skewer to win material.';
        }
        
        // Check if next move might be checkmate
        if (currentMove && currentMove.includes('#')) {
            return 'Find the checkmate!';
        }
        
        if (currentMove && currentMove.includes('+')) {
            return 'Continue with check - maintain the pressure.';
        }
        
        return 'Find the powerful follow-up move.';
    }
    
    // Move 3+ (Final moves)
    if (currentMoveIndex >= 2) {
        if (themeSet.has('mate') || themeSet.has('matein3')) {
            return 'Finish the combination - deliver checkmate!';
        }
        
        if (themeSet.has('promotion') || themeSet.has('advancedpawn')) {
            return 'Promote and secure the win.';
        }
        
        if (currentMove && currentMove.includes('#')) {
            return 'Checkmate!';
        }
        
        return 'Complete the winning sequence.';
    }
    
    // Fallback
    return puzzle.hint || 'Find the best move.';
}

/**
 * Get hint progress indicator for UI
 */
export function getHintProgress(totalMoves: number, currentMoveIndex: number): string {
    if (totalMoves <= 1) return '';
    return `Move ${currentMoveIndex + 1} of ${totalMoves}`;
}
